import React, { useState, useEffect, useCallback } from 'react';
import { X, Search, Satellite, Loader2, Plus } from 'lucide-react';

const SECTION_COLOR = '#0CC0DF';

// POZOR: Field MUSÍ být mimo parent funkci. Definovat ho dovnitř = nová ref
// na každý render → React unmount/remount celý subtree → input ztratí focus.
function Field({ label, hint, children, withLookup, lookupKey, labelColor, lookupBusy, lookupDisabled, onLookup }) {
    return (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{
                fontSize: 10, fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: 0.5,
                color: labelColor,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
                <span>{label}</span>
                {withLookup && (
                    <button type="button"
                        onClick={() => onLookup?.(lookupKey)}
                        disabled={lookupBusy === lookupKey || lookupDisabled}
                        style={{
                            background: 'rgba(12,192,223,0.10)',
                            color: SECTION_COLOR,
                            border: '1px solid rgba(12,192,223,0.30)',
                            borderRadius: 5, padding: '2px 8px',
                            fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
                            letterSpacing: 0.5,
                            cursor: lookupBusy === lookupKey || lookupDisabled ? 'not-allowed' : 'pointer',
                            opacity: lookupBusy === lookupKey || lookupDisabled ? 0.5 : 1,
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                        }}>
                        {lookupBusy === lookupKey
                            ? <Loader2 size={9} style={{ animation: 'spin 1s linear infinite' }} />
                            : <Search size={9} />}
                        Registr
                    </button>
                )}
            </span>
            {children}
            {hint && (
                <span style={{ fontSize: 10, color: labelColor, lineHeight: 1.4 }}>{hint}</span>
            )}
        </label>
    );
}

export default function InspectorCreate({ onClose, onSaved, isDark }) {
    const [form, setForm] = useState({
        spz: '',
        model: '',
        rok_vyroby: new Date().getFullYear(),
        typ_vozidla_id: '',
        typ_paliva_id: '',
        barva: '',
        vin: '',
        orv: '',
        tp: '',
        km_stav: 0,
        km_servis_interval: '',
        traccar_device_id: '',
        poznamka: '',
    });
    const [ciselniky, setCiselniky] = useState({ typy_vozidel: [], typy_paliv: [], typy_servisu: [] });
    const [traccarDevices, setTraccarDevices] = useState([]);
    const [loadingTraccar, setLoadingTraccar] = useState(false);
    const [lookupBusy, setLookupBusy] = useState(null);
    const [lookupMsg, setLookupMsg] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetch('/api/flotila/ciselniky')
            .then((r) => r.json())
            .then((d) => {
                setCiselniky(d || {});
                setForm((f) => ({
                    ...f,
                    typ_vozidla_id: d?.typy_vozidel?.[0]?.id || '',
                    typ_paliva_id: d?.typy_paliv?.[0]?.id || '',
                }));
            })
            .catch(() => {});

        setLoadingTraccar(true);
        fetch('/api/flotila/gps/traccar-devices')
            .then((r) => r.json())
            .then((d) => setTraccarDevices(Array.isArray(d) ? d : []))
            .catch(() => {})
            .finally(() => setLoadingTraccar(false));
    }, []);

    const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content || '';

    const handleLookup = useCallback(async (typ) => {
        const value = form[typ]?.trim();
        if (!value) return;
        setLookupBusy(typ);
        setLookupMsg(null);
        try {
            const r = await fetch(`/api/flotila/registr-lookup?${typ}=${encodeURIComponent(value)}`);
            const data = await r.json();
            if (!r.ok) {
                setLookupMsg({ ok: false, text: data?.error || 'Lookup selhal' });
                return;
            }
            const mapped = {};
            if (data.spz && !form.spz) mapped.spz = data.spz;
            if (data.model) mapped.model = data.model;
            if (data.rok_vyroby) mapped.rok_vyroby = data.rok_vyroby;
            if (data.barva) mapped.barva = data.barva;
            if (data.vin && !form.vin) mapped.vin = data.vin;
            if (data.orv && !form.orv) mapped.orv = data.orv;
            if (data.tp && !form.tp) mapped.tp = data.tp;
            if (data.typ_paliva_id) mapped.typ_paliva_id = data.typ_paliva_id;
            if (data.typ_vozidla_id) mapped.typ_vozidla_id = data.typ_vozidla_id;
            setForm((f) => ({ ...f, ...mapped }));
            setLookupMsg({ ok: true, text: `Registr našel: ${data.model || data.spz || 'data doplněna'}` });
        } catch (err) {
            setLookupMsg({ ok: false, text: 'Spojení s registrem selhalo' });
        } finally {
            setLookupBusy(null);
        }
    }, [form]);

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        try {
            const payload = { ...form };
            ['vin', 'orv', 'tp', 'traccar_device_id', 'barva', 'poznamka'].forEach((k) => {
                if (payload[k] === '') payload[k] = null;
            });
            payload.km_stav = Number(payload.km_stav || 0);
            payload.km_servis_interval = payload.km_servis_interval ? Number(payload.km_servis_interval) : null;
            payload.rok_vyroby = Number(payload.rok_vyroby);

            const r = await fetch('/api/flotila/vozidla', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': csrf(),
                    'Accept': 'application/json',
                },
                credentials: 'same-origin',
                body: JSON.stringify(payload),
            });
            if (!r.ok) {
                const data = await r.json().catch(() => ({}));
                const msg = data?.message || data?.error
                    || (data?.errors ? Object.values(data.errors).flat().join('\n') : 'Uložení selhalo');
                throw new Error(msg);
            }
            const created = await r.json();
            onSaved?.(created);
        } catch (err) {
            setError(err.message || 'Uložení selhalo');
        } finally {
            setSaving(false);
        }
    };

    const labelColor = isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)';
    const subtle = isDark ? 'rgba(255,255,255,0.65)' : 'var(--text-secondary)';
    const textColor = isDark ? '#fff' : 'var(--text-primary)';
    const inputBorder = isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)';
    const inputBg = isDark ? 'rgba(0,0,0,0.3)' : 'var(--surface-base)';
    const headerBg = isDark
        ? 'linear-gradient(135deg, rgba(12,192,223,0.10), rgba(12,192,223,0.02))'
        : 'linear-gradient(135deg, rgba(12,192,223,0.08), rgba(12,192,223,0.02))';
    const closeHover = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)';

    const inputStyle = {
        width: '100%',
        background: inputBg,
        color: textColor,
        border: `1px solid ${inputBorder}`,
        borderRadius: 7,
        padding: '8px 10px',
        fontSize: 13,
        outline: 'none',
        fontFamily: 'inherit',
        boxSizing: 'border-box',
    };
    const monoStyle = { ...inputStyle, fontVariantNumeric: 'tabular-nums', letterSpacing: 0.3 };

    // Pomocné props pro Field — předáváme jen to, co Field potřebuje, místo aby
    // si to bral ze closure (Field je teď mimo komponentu, žádný closure nemá).
    const fieldCommon = { labelColor, lookupBusy, onLookup: handleLookup };

    const traccarSelected = traccarDevices.find((d) => String(d.id) === String(form.traccar_device_id));
    const traccarConflict = traccarSelected && traccarSelected.vozidlo_id;

    return (
        <form onSubmit={submit} style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            minHeight: 0,
        }}>
            <div style={{
                background: headerBg,
                borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)'}`,
                padding: '14px 18px',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 12,
            }}>
                <div style={{ minWidth: 0 }}>
                    <div style={{
                        display: 'inline-flex', alignItems: 'center', gap: 8,
                        fontSize: 18, fontWeight: 800,
                        letterSpacing: -0.2,
                        color: textColor,
                    }}>
                        <Plus size={18} style={{ color: SECTION_COLOR }} />
                        Nové vozidlo
                    </div>
                    <div style={{ fontSize: 12, color: subtle, marginTop: 4 }}>
                        Lookup z registru MD ČR doplní data dle VIN / ORV / TP. Traccar zařízení můžeš přiřadit hned, nebo později.
                    </div>
                </div>
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Zavřít"
                    style={{
                        background: 'transparent', border: 'none',
                        color: labelColor, cursor: 'pointer',
                        padding: 6, borderRadius: 6, display: 'flex',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = closeHover; e.currentTarget.style.color = textColor; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = labelColor; }}
                >
                    <X size={18} />
                </button>
            </div>

            <div style={{
                padding: '14px 18px',
                overflowY: 'auto',
                flex: 1,
                display: 'flex', flexDirection: 'column', gap: 12,
                color: textColor,
            }}>
                {/* Identifikace */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <Field label="SPZ *" {...fieldCommon}>
                        <input style={monoStyle} value={form.spz} onChange={change('spz')} required maxLength={10} />
                    </Field>
                    <Field label="Rok výroby *" {...fieldCommon}>
                        <input style={monoStyle} type="number" value={form.rok_vyroby} onChange={change('rok_vyroby')} required min="1990" max={new Date().getFullYear() + 1} />
                    </Field>
                </div>
                <Field label="Model *" {...fieldCommon}>
                    <input style={inputStyle} value={form.model} onChange={change('model')} required maxLength={100} />
                </Field>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <Field label="Typ vozidla *" {...fieldCommon}>
                        <select style={inputStyle} value={form.typ_vozidla_id} onChange={change('typ_vozidla_id')} required>
                            <option value="">— vyber —</option>
                            {ciselniky.typy_vozidel?.map((t) => (
                                <option key={t.id} value={t.id}>{t.nazev}</option>
                            ))}
                        </select>
                    </Field>
                    <Field label="Typ paliva *" {...fieldCommon}>
                        <select style={inputStyle} value={form.typ_paliva_id} onChange={change('typ_paliva_id')} required>
                            <option value="">— vyber —</option>
                            {ciselniky.typy_paliv?.map((t) => (
                                <option key={t.id} value={t.id}>{t.nazev}</option>
                            ))}
                        </select>
                    </Field>
                </div>

                <Field label="Barva" {...fieldCommon}>
                    <input style={inputStyle} value={form.barva} onChange={change('barva')} maxLength={50} />
                </Field>

                {/* Registr lookup */}
                <div style={{
                    padding: 12, borderRadius: 9,
                    background: isDark ? 'rgba(12,192,223,0.06)' : 'rgba(12,192,223,0.04)',
                    border: `1px solid ${isDark ? 'rgba(12,192,223,0.20)' : 'rgba(12,192,223,0.25)'}`,
                    display: 'flex', flexDirection: 'column', gap: 10,
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: SECTION_COLOR, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        <Search size={11} /> Lookup z registru (Datová kostka MD ČR)
                    </div>
                    <Field label="VIN" withLookup lookupKey="vin" {...fieldCommon} lookupDisabled={!form.vin}>
                        <input style={monoStyle} value={form.vin} onChange={change('vin')} maxLength={20} />
                    </Field>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                        <Field label="ORV" withLookup lookupKey="orv" {...fieldCommon} lookupDisabled={!form.orv}>
                            <input style={monoStyle} value={form.orv} onChange={change('orv')} maxLength={20} />
                        </Field>
                        <Field label="TP" withLookup lookupKey="tp" {...fieldCommon} lookupDisabled={!form.tp}>
                            <input style={monoStyle} value={form.tp} onChange={change('tp')} maxLength={20} />
                        </Field>
                    </div>
                    {lookupMsg && (
                        <div style={{
                            fontSize: 11, padding: '6px 10px', borderRadius: 6,
                            background: lookupMsg.ok ? 'rgba(16,185,129,0.10)' : 'rgba(239,68,68,0.10)',
                            color: lookupMsg.ok ? '#10B981' : '#EF4444',
                            border: `1px solid ${lookupMsg.ok ? 'rgba(16,185,129,0.25)' : 'rgba(239,68,68,0.25)'}`,
                        }}>
                            {lookupMsg.text}
                        </div>
                    )}
                </div>

                {/* Provoz */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <Field label="Tachometr (km)" {...fieldCommon}>
                        <input style={monoStyle} type="number" min="0" value={form.km_stav} onChange={change('km_stav')} />
                    </Field>
                    <Field label="Servisní interval (km)" {...fieldCommon}>
                        <input style={monoStyle} type="number" min="0" value={form.km_servis_interval} onChange={change('km_servis_interval')} />
                    </Field>
                </div>

                {/* Traccar */}
                <div style={{
                    padding: 12, borderRadius: 9,
                    background: isDark ? 'rgba(151,90,205,0.07)' : 'rgba(151,90,205,0.05)',
                    border: `1px solid ${isDark ? 'rgba(151,90,205,0.25)' : 'rgba(151,90,205,0.30)'}`,
                    display: 'flex', flexDirection: 'column', gap: 8,
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#975ACD', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        <Satellite size={11} /> Traccar GPS tracker
                    </div>
                    <Field label="Zařízení" {...fieldCommon} hint={
                        loadingTraccar ? 'Načítám zařízení z Traccaru…'
                            : traccarDevices.length === 0 ? 'Žádné Traccar zařízení dostupné. Můžeš přiřadit později v nastavení vozidla.'
                            : 'Volné zařízení = bez vozidla. Přiřazené = bude přesunuto z původního vozidla.'
                    }>
                        <select style={inputStyle} value={form.traccar_device_id} onChange={change('traccar_device_id')}>
                            <option value="">— Bez Traccaru (později) —</option>
                            {traccarDevices.map((d) => {
                                const obsazeno = d.vozidlo_id ? ` · ${d.vozidlo_spz || 'přiřazeno'}` : ' · Volné';
                                return (
                                    <option key={d.id} value={d.id}>
                                        {d.name || d.id}{obsazeno}{d.status ? ` · ${d.status}` : ''}
                                    </option>
                                );
                            })}
                        </select>
                    </Field>
                    {traccarConflict && (
                        <div style={{
                            fontSize: 11, padding: '6px 10px', borderRadius: 6,
                            background: 'rgba(255,200,87,0.10)',
                            color: '#FFC857',
                            border: '1px solid rgba(255,200,87,0.25)',
                        }}>
                            ⚠ Toto zařízení je přiřazeno k {traccarConflict.vozidlo_spz || `vozidlu #${traccarConflict.vozidlo_id}`}. Uložením se z něj odebere.
                        </div>
                    )}
                </div>

                <Field label="Poznámka" {...fieldCommon}>
                    <textarea
                        style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }}
                        value={form.poznamka}
                        onChange={change('poznamka')}
                    />
                </Field>

                {error && (
                    <div style={{
                        padding: '8px 12px', borderRadius: 7, fontSize: 12,
                        background: 'rgba(239,68,68,0.12)', color: '#EF4444',
                        border: '1px solid rgba(239,68,68,0.30)',
                        whiteSpace: 'pre-wrap',
                    }}>
                        {error}
                    </div>
                )}
            </div>

            <div style={{
                padding: '12px 18px',
                borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)'}`,
                display: 'flex', justifyContent: 'flex-end', gap: 8,
                background: isDark ? 'rgba(0,0,0,0.15)' : 'rgba(0,0,0,0.02)',
            }}>
                <button type="button" onClick={onClose} style={{
                    background: 'transparent', color: subtle,
                    border: `1px solid ${inputBorder}`,
                    padding: '9px 14px', borderRadius: 7,
                    fontSize: 13, fontWeight: 600, cursor: 'pointer',
                }}>
                    Zrušit
                </button>
                <button type="submit" disabled={saving} style={{
                    background: 'linear-gradient(135deg, #1D9E75, #10B981)',
                    color: '#fff', border: 'none',
                    padding: '9px 18px', borderRadius: 7,
                    fontSize: 13, fontWeight: 700,
                    cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.6 : 1,
                }}>
                    {saving ? 'Ukládám…' : 'Uložit vozidlo'}
                </button>
            </div>
        </form>
    );
}
